import type { RequirementProgressItem } from '@starter/shared';

const MINIMUM_RULE_TYPES = new Set([
  'TOTAL_MINIMUM',
  'CATEGORY_MINIMUM',
  'ROLE_MINIMUM',
  'CATEGORY_ROLE_MINIMUM',
  'DISTINCT_CATEGORY_MINIMUM',
  'SPECIALTY_SET_MINIMUM',
  'SPECIALTY_SET_ROLE_MINIMUM',
  'DISTINCT_SPECIALTY_THRESHOLD_MINIMUM',
]);

const MAXIMUM_RULE_TYPES = new Set([
  'CATEGORY_MAXIMUM',
  'ROLE_MAXIMUM',
  'CATEGORY_ROLE_MAXIMUM',
  'COUNT_CAP',
]);

const RULE_TYPE_LABELS: Record<string, string> = {
  TOTAL_MINIMUM: 'Total cases — minimum',
  CATEGORY_MINIMUM: 'Specialty category — minimum',
  ROLE_MINIMUM: 'Clinical role — minimum',
  CATEGORY_ROLE_MINIMUM: 'Specialty and role — minimum',
  DISTINCT_CATEGORY_MINIMUM: 'Distinct specialties — minimum',
  CATEGORY_MAXIMUM: 'Specialty category — maximum',
  ROLE_MAXIMUM: 'Clinical role — maximum',
  CATEGORY_ROLE_MAXIMUM: 'Specialty and role — maximum',
  COUNT_CAP: 'Approved cases — cap',
};

function humanizeRuleType(ruleType: string): string {
  return ruleType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function requirementRuleLabel(ruleType: string): string {
  return RULE_TYPE_LABELS[ruleType] ?? humanizeRuleType(ruleType);
}

export function requirementProgressIsIndeterminate(item: RequirementProgressItem): boolean {
  return (
    item.met === null ||
    item.evaluation === 'UNEVAULABLE_RULE_TYPE' ||
    item.evaluation === 'UNEVAULABLE_INVALID_CONFIG'
  );
}

export function requirementProgressStateLabel(item: RequirementProgressItem): string {
  if (requirementProgressIsIndeterminate(item)) return 'Needs review';
  if (item.met === true) return 'Met';
  if (item.met === false) return 'Not met';
  return 'Needs review';
}

export function requirementProgressStateVariant(
  item: RequirementProgressItem,
): 'default' | 'success' | 'warning' | 'muted' {
  if (requirementProgressIsIndeterminate(item)) return 'muted';
  if (item.met === true) return 'success';
  if (item.met === false) return 'warning';
  return 'muted';
}

export function requirementProgressShowsBar(item: RequirementProgressItem): boolean {
  return (
    item.evaluation === 'EVALUATED' &&
    item.met !== null &&
    item.threshold !== null &&
    item.threshold > 0 &&
    MINIMUM_RULE_TYPES.has(item.ruleType)
  );
}

export function requirementProgressThresholdText(item: RequirementProgressItem): string | null {
  if (item.threshold === null) return null;
  if (MINIMUM_RULE_TYPES.has(item.ruleType)) {
    return `Required minimum: ${item.threshold}`;
  }
  if (MAXIMUM_RULE_TYPES.has(item.ruleType) || item.ruleType === 'COUNT_CAP') {
    return `Cap: ${item.threshold}`;
  }
  return `Threshold: ${item.threshold}`;
}

export function requirementProgressBarPercent(item: RequirementProgressItem): number {
  if (!requirementProgressShowsBar(item) || item.threshold === null || item.threshold <= 0) return 0;
  return Math.min(100, Math.round((item.current / item.threshold) * 100));
}
